import { MoreVerticalIcon, PlayIcon, EyeIcon, PencilIcon, TrashIcon } from "lucide-react"
import { Button } from "@/components/ui/button"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { Badge } from "@/components/ui/badge"
import { cn } from "@/lib/utils"

export interface Video {
  id: string
  title: string
  description?: string
  videoUrl: string
  thumbnail: string
  duration: string
  viewCount: number
  status: "published" | "draft"
  createdAt?: string
  transcript?: string
}

interface VideoCardProps {
  video: Video
  onEdit?: (video: Video) => void
  onPreview?: (video: Video) => void
  onDelete?: (video: Video) => void
  className?: string
}

export function VideoCard({ video, onEdit, onPreview, onDelete, className }: VideoCardProps) {
  return (
    <div className={cn("bg-card border border-border rounded-lg overflow-hidden hover:shadow-md transition-shadow", className)}>
      {/* Thumbnail */}
      <div className="relative aspect-video bg-muted group cursor-pointer" onClick={() => onPreview?.(video)}>
        <img
          src={video.thumbnail}
          alt={video.title}
          className="w-full h-full object-cover"
        />
        {/* Overlay on hover */}
        <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
          <PlayIcon className="w-12 h-12 text-white" />
        </div>
        {/* Duration badge */}
        <div className="absolute bottom-2 right-2 bg-black/80 text-white text-xs px-2 py-1 rounded">
          {video.duration}
        </div>
      </div>

      {/* Content */}
      <div className="p-4">
        <div className="flex items-start justify-between gap-2 mb-2">
          <h3 className="font-semibold text-foreground line-clamp-2 flex-1">
            {video.title}
          </h3>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                <MoreVerticalIcon className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => onEdit?.(video)}>
                <PencilIcon className="h-4 w-4 mr-2" />
                Edit
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => onPreview?.(video)}>
                <EyeIcon className="h-4 w-4 mr-2" />
                Preview
              </DropdownMenuItem>
              <DropdownMenuItem 
                onClick={() => onDelete?.(video)}
                className="text-destructive focus:text-destructive"
              >
                <TrashIcon className="h-4 w-4 mr-2" />
                Delete
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        {/* Metadata */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3 text-sm text-muted-foreground">
            <span className="flex items-center gap-1">
              <EyeIcon className="h-4 w-4" />
              {video.viewCount.toLocaleString()}
            </span>
          </div>
          <Badge 
            variant={video.status === "published" ? "default" : "secondary"}
            className={cn(
              video.status === "published" && "bg-green-500 hover:bg-green-600 text-white",
              video.status === "draft" && "bg-muted text-muted-foreground"
            )}
          >
            {video.status === "published" ? "Published" : "Draft"}
          </Badge>
        </div>
      </div>
    </div>
  )
}